/* AppDocCard — read-side visual for the app document: the eight pointer
 * facts (APP.md §2) as a tidy card. The app is an identity card, not a
 * machine — so the visual is literally the card. */
import React, {
    useMemo
} from 'react';
import './visuals.css';

interface Fact {
    label: string;
    value: React.ReactNode;
    missing: boolean;
}

function fact(label: string, raw: any): Fact {
    const missing = raw == null || raw === '';
    return {
        label,
        value: missing ? '—' : String(raw),
        missing
    };
}

export default function AppDocCard({
    docText,
    journeys: liveJourneys
}: {
    docText: string;
    /** Live journey ids — overrides the (possibly stale) stored app.journeys[]
     * so a newly added journey shows here without editing the app doc. */
    journeys?: string[];
}) {
    const parsed = useMemo(() => {
        try {
            return {
                doc: JSON.parse(docText),
                err: null as string | null
            };
        } catch (e: any) {
            return {
                doc: null,
                err: e?.message || 'invalid JSON'
            };
        }
    }, [docText]);

    if (parsed.err) {
        return (
            <div className="aps-empty" style={{
                padding: '40px 20px'
            }}>
                <span>Cannot draw — the JSON does not parse ({parsed.err}).
                    Fix it in the JSON view.</span>
            </div>
        );
    }

    const doc = parsed.doc || {};
    const journeys: string[] = liveJourneys
        ? liveJourneys.map(String)
        : Array.isArray(doc.journeys) ? doc.journeys.map(String) : [];
    const facts: Fact[] = [
        fact('App id', doc.id),
        fact('Name', doc.name),
        fact('Description', doc.description),
        fact('Org', doc.org),
        fact('Identity source', doc.identity_source),
        fact('Default role', doc.default_role),
        fact('Session workflow', doc.session?.workflow),
        fact('Landing journey', doc.landing_journey),
        fact('Home journey', doc.home_journey)
    ];

    return (
        <div className="aps-appdoc">
            <div className="aps-appdoc-card">
                {facts.map((f) => (
                    <div key={f.label} className="aps-appdoc-row">
                        <span className="aps-appdoc-key">{f.label}</span>
                        <span className={'aps-appdoc-val' + (f.missing ? ' missing' : '')}>
                            {f.value}
                        </span>
                    </div>
                ))}
                <div className="aps-appdoc-row">
                    <span className="aps-appdoc-key">Journeys</span>
                    {journeys.length === 0 ? (
                        <span className="aps-appdoc-val missing">—</span>
                    ) : (
                        <span className="aps-chip-row">
                            {journeys.map((j) => (
                                <span key={j} className="aps-badge aps-badge-indigo">{j}</span>
                            ))}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
